import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Package, TrendingDown, Calendar, Camera, Upload, BarChart3, Mail, RefreshCw, Search, ChevronDown, ChevronUp, Clock, DollarSign, AlertCircle, CheckCircle, XCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Inventory() {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('alerts');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [movements, setMovements] = useState([]);
  const [predictions, setPredictions] = useState([]);
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showMovementModal, setShowMovementModal] = useState(false);
  const [showScanModal, setShowScanModal] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [scanning, setScanning] = useState(false);
  const fileInputRef = useRef(null);
  
  const [movementForm, setMovementForm] = useState({
    product_id: '',
    movement_type: 'sale',
    quantity: 1,
    reason: '',
    ticket_number: ''
  });

  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) return;
    
    if (!user || !['admin', 'cajera'].includes(user.role)) {
      navigate('/');
      return;
    }
    fetchData();
  }, [user, authLoading, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [statsRes, alertsRes, movementsRes, productsRes] = await Promise.all([
        fetch(`${API_URL}/api/inventory/stats`, { headers }),
        fetch(`${API_URL}/api/inventory/alerts`, { headers }),
        fetch(`${API_URL}/api/inventory/movements?limit=50`, { headers }),
        fetch(`${API_URL}/api/products?limit=1000`, { headers })
      ]);
      
      if (statsRes.ok) setStats(await statsRes.json());
      if (alertsRes.ok) setAlerts(await alertsRes.json());
      if (movementsRes.ok) setMovements(await movementsRes.json());
      if (productsRes.ok) setProducts(await productsRes.json());
      
    } catch (error) {
      console.error('Error fetching inventory data:', error);
    }
    setLoading(false);
  };

  const fetchPredictions = async () => {
    try {
      const res = await fetch(`${API_URL}/api/inventory/predictions?days_lookback=30`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setPredictions(await res.json());
    } catch (error) {
      console.error('Error fetching predictions:', error);
    }
  };

  const handleCreateMovement = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_URL}/api/inventory/movement`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(movementForm)
      });
      
      if (res.ok) {
        setShowMovementModal(false);
        setMovementForm({ product_id: '', movement_type: 'sale', quantity: 1, reason: '', ticket_number: '' });
        setSelectedProduct(null);
        fetchData();
      }
    } catch (error) {
      console.error('Error creating movement:', error);
    }
  };

  const handleScanTicket = async (base64Image) => {
    setScanning(true);
    setScanResult(null);
    try {
      const res = await fetch(`${API_URL}/api/inventory/scan-ticket`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ ticket_image: base64Image })
      });
      
      const result = await res.json();
      setScanResult(result);
      if (result.success) {
        fetchData();
      }
    } catch (error) {
      console.error('Error scanning ticket:', error);
      setScanResult({ success: false, error: 'Error al procesar el ticket' });
    }
    setScanning(false);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result.split(',')[1];
        handleScanTicket(base64);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSendAlerts = async () => {
    try {
      const res = await fetch(`${API_URL}/api/inventory/send-alerts-email`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await res.json();
      alert(result.message);
    } catch (error) {
      console.error('Error sending alerts:', error);
    }
  };

  const filteredProducts = products.filter(p => 
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (p.barcode && p.barcode.includes(searchTerm))
  );

  const getAlertIcon = (type) => {
    switch (type) {
      case 'expired': return <XCircle className="w-5 h-5 text-red-500" />;
      case 'expiring_soon': return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      case 'low_stock': return <TrendingDown className="w-5 h-5 text-blue-500" />;
      default: return <AlertTriangle className="w-5 h-5 text-gray-500" />;
    }
  };

  const getAlertBg = (type) => {
    switch (type) {
      case 'expired': return 'bg-red-50 border-red-200';
      case 'expiring_soon': return 'bg-yellow-50 border-yellow-200';
      case 'low_stock': return 'bg-blue-50 border-blue-200';
      default: return 'bg-gray-50 border-gray-200';
    }
  };

  const getMovementColor = (type) => {
    switch (type) {
      case 'sale': return 'text-red-600 bg-red-100';
      case 'restock': return 'text-green-600 bg-green-100';
      case 'return': return 'text-blue-600 bg-blue-100';
      case 'loss': return 'text-orange-600 bg-orange-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getMovementLabel = (type) => {
    const labels = {
      sale: 'Venta',
      restock: 'Reabastecimiento',
      return: 'Devolución',
      loss: 'Merma',
      adjustment: 'Ajuste'
    };
    return labels[type] || type;
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-2" />
          <p className="text-gray-600">Cargando inventario...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8" data-testid="inventory-page">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Package className="w-8 h-8 text-blue-600" />
              Inventario Inteligente
            </h1>
            <p className="text-gray-600 mt-1">Control y predicción de stock con IA</p>
          </div>
          <div className="flex gap-3 mt-4 md:mt-0">
            <button
              onClick={() => setShowScanModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              data-testid="scan-ticket-btn"
            >
              <Camera className="w-5 h-5" />
              Escanear Ticket
            </button>
            <button
              onClick={handleSendAlerts}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              data-testid="send-alerts-btn"
            >
              <Mail className="w-5 h-5" />
              Enviar Alertas
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8" data-testid="inventory-stats">
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200" data-testid="stat-total-products">
              <div className="text-sm text-gray-500">Total Productos</div>
              <div className="text-2xl font-bold text-gray-900">{stats.total_products}</div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200" data-testid="stat-stock-value">
              <div className="text-sm text-gray-500">Valor Inventario</div>
              <div className="text-2xl font-bold text-green-600">${stats.total_stock_value?.toLocaleString()}</div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200" data-testid="stat-low-stock">
              <div className="text-sm text-gray-500">Bajo Stock</div>
              <div className="text-2xl font-bold text-yellow-600">{stats.low_stock_count}</div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200" data-testid="stat-out-of-stock">
              <div className="text-sm text-gray-500">Sin Stock</div>
              <div className="text-2xl font-bold text-red-600">{stats.out_of_stock_count}</div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200" data-testid="stat-expiring">
              <div className="text-sm text-gray-500">Por Caducar</div>
              <div className="text-2xl font-bold text-orange-600">{stats.expiring_soon_count}</div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200" data-testid="stat-today-sales">
              <div className="text-sm text-gray-500">Ventas Hoy</div>
              <div className="text-2xl font-bold text-blue-600">{stats.today_sales_units} uds</div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
          <div className="flex border-b border-gray-200 overflow-x-auto">
            {[
              { id: 'alerts', label: 'Alertas', icon: AlertTriangle, count: alerts.length },
              { id: 'movements', label: 'Movimientos', icon: Clock },
              { id: 'predictions', label: 'Predicciones IA', icon: BarChart3 },
              { id: 'products', label: 'Productos', icon: Package }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  if (tab.id === 'predictions' && predictions.length === 0) {
                    fetchPredictions();
                  }
                }}
                className={`flex items-center gap-2 px-6 py-4 font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon className="w-5 h-5" />
                {tab.label}
                {tab.count !== undefined && (
                  <span className={`px-2 py-0.5 rounded-full text-xs ${
                    tab.count > 0 ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          <div className="p-6">
            {/* Alerts Tab */}
            {activeTab === 'alerts' && (
              <div className="space-y-4">
                {alerts.length === 0 ? (
                  <div className="text-center py-12">
                    <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-900">Todo en orden</h3>
                    <p className="text-gray-600">No hay alertas de inventario activas</p>
                  </div>
                ) : (
                  alerts.map(alert => (
                    <div
                      key={alert.id}
                      className={`flex items-start gap-4 p-4 rounded-lg border ${getAlertBg(alert.alert_type)}`}
                    >
                      {getAlertIcon(alert.alert_type)}
                      <div className="flex-1">
                        <div className="font-semibold text-gray-900">{alert.product_name}</div>
                        <div className="text-sm text-gray-600 mt-1">{alert.suggested_action}</div>
                        <div className="flex gap-4 mt-2 text-sm">
                          <span className="text-gray-500">Stock: <strong>{alert.current_stock}</strong></span>
                          {alert.expiration_date && (
                            <span className="text-gray-500">Caduca: <strong>{alert.expiration_date}</strong></span>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          setSelectedProduct(products.find(p => p.id === alert.product_id));
                          setMovementForm({ ...movementForm, product_id: alert.product_id, movement_type: 'restock' });
                          setShowMovementModal(true);
                        }}
                        className="px-3 py-1 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
                      >
                        Reabastecer
                      </button>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Movements Tab */}
            {activeTab === 'movements' && (
              <div className="space-y-3">
                <div className="flex justify-end mb-4">
                  <button
                    onClick={() => setShowMovementModal(true)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    + Nuevo Movimiento
                  </button>
                </div>
                {movements.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No hay movimientos registrados</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="text-left text-sm text-gray-500 border-b">
                          <th className="pb-3 font-medium">Fecha</th>
                          <th className="pb-3 font-medium">Producto</th>
                          <th className="pb-3 font-medium">Tipo</th>
                          <th className="pb-3 font-medium">Cantidad</th>
                          <th className="pb-3 font-medium">Stock</th>
                          <th className="pb-3 font-medium">Usuario</th>
                        </tr>
                      </thead>
                      <tbody>
                        {movements.map(mov => (
                          <tr key={mov.id} className="border-b border-gray-100">
                            <td className="py-3 text-sm text-gray-600">
                              {new Date(mov.created_at).toLocaleString('es-MX', { 
                                day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' 
                              })}
                            </td>
                            <td className="py-3 font-medium text-gray-900">{mov.product_name}</td>
                            <td className="py-3">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getMovementColor(mov.movement_type)}`}>
                                {getMovementLabel(mov.movement_type)}
                              </span>
                            </td>
                            <td className={`py-3 font-semibold ${mov.quantity < 0 ? 'text-red-600' : 'text-green-600'}`}>
                              {mov.quantity > 0 ? '+' : ''}{mov.quantity}
                            </td>
                            <td className="py-3 text-sm text-gray-600">
                              {mov.stock_before} → {mov.stock_after}
                            </td>
                            <td className="py-3 text-sm text-gray-600">{mov.created_by_name}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Predictions Tab */}
            {activeTab === 'predictions' && (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <div className="flex items-start gap-3">
                    <BarChart3 className="w-6 h-6 text-blue-600 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-blue-900">Predicciones basadas en IA</h4>
                      <p className="text-sm text-blue-700">
                        Análisis de los últimos 30 días de ventas para predecir necesidades de reabastecimiento
                      </p>
                    </div>
                  </div>
                </div>
                
                {predictions.length === 0 ? (
                  <div className="text-center py-8">
                    <RefreshCw className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-2" />
                    <p className="text-gray-600">Calculando predicciones...</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="text-left text-sm text-gray-500 border-b">
                          <th className="pb-3 font-medium">Producto</th>
                          <th className="pb-3 font-medium">Stock Actual</th>
                          <th className="pb-3 font-medium">Venta Diaria Prom.</th>
                          <th className="pb-3 font-medium">Días hasta Agotarse</th>
                          <th className="pb-3 font-medium">Reabastecer</th>
                          <th className="pb-3 font-medium">Alerta</th>
                        </tr>
                      </thead>
                      <tbody>
                        {predictions.map(pred => (
                          <tr key={pred.product_id} className="border-b border-gray-100">
                            <td className="py-3 font-medium text-gray-900">{pred.product_name}</td>
                            <td className="py-3 text-gray-600">{pred.current_stock}</td>
                            <td className="py-3 text-gray-600">{pred.avg_daily_sales}</td>
                            <td className={`py-3 font-semibold ${
                              pred.days_until_stockout <= 7 ? 'text-red-600' :
                              pred.days_until_stockout <= 14 ? 'text-yellow-600' : 'text-green-600'
                            }`}>
                              {pred.days_until_stockout >= 999 ? 'N/A' : `${pred.days_until_stockout} días`}
                            </td>
                            <td className="py-3">
                              {pred.recommended_reorder_quantity > 0 && (
                                <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                                  +{pred.recommended_reorder_quantity} unidades
                                </span>
                              )}
                            </td>
                            <td className="py-3 text-sm">
                              {pred.expiration_alert && (
                                <span className="text-orange-600">{pred.expiration_alert}</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Products Tab */}
            {activeTab === 'products' && (
              <div>
                <div className="flex gap-4 mb-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Buscar por nombre o código de barras..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-sm text-gray-500 border-b">
                        <th className="pb-3 font-medium">Producto</th>
                        <th className="pb-3 font-medium">Código</th>
                        <th className="pb-3 font-medium">Stock</th>
                        <th className="pb-3 font-medium">Precio</th>
                        <th className="pb-3 font-medium">Costo</th>
                        <th className="pb-3 font-medium">Caducidad</th>
                        <th className="pb-3 font-medium">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredProducts.slice(0, 50).map(product => (
                        <tr key={product.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 font-medium text-gray-900">{product.name}</td>
                          <td className="py-3 text-sm text-gray-600">{product.barcode || '-'}</td>
                          <td className={`py-3 font-semibold ${
                            product.stock <= 0 ? 'text-red-600' :
                            product.stock <= (product.min_stock_alert || 5) ? 'text-yellow-600' : 'text-green-600'
                          }`}>
                            {product.stock}
                          </td>
                          <td className="py-3 text-gray-600">${product.price}</td>
                          <td className="py-3 text-gray-600">{product.cost ? `$${product.cost}` : '-'}</td>
                          <td className="py-3 text-sm text-gray-600">{product.expiration_date || '-'}</td>
                          <td className="py-3">
                            <button
                              onClick={() => {
                                setSelectedProduct(product);
                                setMovementForm({ ...movementForm, product_id: product.id });
                                setShowMovementModal(true);
                              }}
                              className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
                            >
                              Movimiento
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredProducts.length > 50 && (
                    <p className="text-center text-gray-500 mt-4">
                      Mostrando 50 de {filteredProducts.length} productos
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Movement Modal */}
        {showMovementModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                {selectedProduct ? `Movimiento: ${selectedProduct.name}` : 'Nuevo Movimiento'}
              </h3>
              
              <form onSubmit={handleCreateMovement} className="space-y-4">
                {!selectedProduct && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Producto</label>
                    <select
                      value={movementForm.product_id}
                      onChange={(e) => setMovementForm({ ...movementForm, product_id: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">Seleccionar producto...</option>
                      {products.map(p => (
                        <option key={p.id} value={p.id}>{p.name} (Stock: {p.stock})</option>
                      ))}
                    </select>
                  </div>
                )}
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Movimiento</label>
                  <select
                    value={movementForm.movement_type}
                    onChange={(e) => setMovementForm({ ...movementForm, movement_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="sale">Venta</option>
                    <option value="restock">Reabastecimiento</option>
                    <option value="return">Devolución</option>
                    <option value="loss">Merma</option>
                    <option value="adjustment">Ajuste Manual</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cantidad</label>
                  <input
                    type="number"
                    min="1"
                    value={movementForm.quantity}
                    onChange={(e) => setMovementForm({ ...movementForm, quantity: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Razón (opcional)</label>
                  <input
                    type="text"
                    value={movementForm.reason}
                    onChange={(e) => setMovementForm({ ...movementForm, reason: e.target.value })}
                    placeholder="Ej: Compra a proveedor, producto dañado..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">No. Ticket (opcional)</label>
                  <input
                    type="text"
                    value={movementForm.ticket_number}
                    onChange={(e) => setMovementForm({ ...movementForm, ticket_number: e.target.value })}
                    placeholder="Ej: T-12345"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowMovementModal(false);
                      setSelectedProduct(null);
                      setMovementForm({ product_id: '', movement_type: 'sale', quantity: 1, reason: '', ticket_number: '' });
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Guardar
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Scan Ticket Modal */}
        {showScanModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Camera className="w-6 h-6 text-green-600" />
                Escanear Ticket de Venta
              </h3>
              
              <p className="text-gray-600 mb-4">
                Sube una foto del ticket de venta y la IA identificará automáticamente los productos vendidos para descontar del inventario.
              </p>
              
              <input
                type="file"
                ref={fileInputRef}
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
              />
              
              {!scanning && !scanResult && (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full py-8 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
                >
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-600">Haz clic para subir imagen</p>
                  <p className="text-sm text-gray-400">JPG, PNG hasta 5MB</p>
                </button>
              )}
              
              {scanning && (
                <div className="text-center py-8">
                  <RefreshCw className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
                  <p className="text-gray-600">Analizando ticket con IA...</p>
                  <p className="text-sm text-gray-400">Esto puede tomar unos segundos</p>
                </div>
              )}
              
              {scanResult && (
                <div className={`p-4 rounded-lg ${scanResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                  {scanResult.success ? (
                    <>
                      <div className="flex items-center gap-2 text-green-700 font-semibold mb-2">
                        <CheckCircle className="w-5 h-5" />
                        Ticket procesado exitosamente
                      </div>
                      {scanResult.ticket_number && (
                        <p className="text-sm text-gray-600">Ticket: {scanResult.ticket_number}</p>
                      )}
                      {scanResult.movements_created?.length > 0 && (
                        <div className="mt-2">
                          <p className="text-sm font-medium text-gray-700">Productos descontados:</p>
                          <ul className="text-sm text-gray-600 mt-1">
                            {scanResult.movements_created.map((m, i) => (
                              <li key={i}>• {m.product_name} (-{m.quantity}) → Stock: {m.stock_after}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {scanResult.products_not_found?.length > 0 && (
                        <div className="mt-2">
                          <p className="text-sm font-medium text-orange-700">No encontrados:</p>
                          <ul className="text-sm text-orange-600 mt-1">
                            {scanResult.products_not_found.map((p, i) => (
                              <li key={i}>• {p.nombre} (x{p.cantidad})</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="flex items-center gap-2 text-red-700">
                      <XCircle className="w-5 h-5" />
                      {scanResult.error || 'Error al procesar el ticket'}
                    </div>
                  )}
                </div>
              )}
              
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowScanModal(false);
                    setScanResult(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cerrar
                </button>
                {scanResult && (
                  <button
                    onClick={() => {
                      setScanResult(null);
                      fileInputRef.current?.click();
                    }}
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    Escanear Otro
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
